import React from 'react';

/**
 * The Follow toggle: two rings stacked one above the other, just apart when
 * off, sliding together to overlap like linked rings when on (.follow-toggle
 * in App.css). One state, shown in the chord chart's header and in the MIDI
 * player, both of its views.
 *
 * Follow on links the chart and the file: they share one place, and the
 * chart keeps matching the player's regions. Off, each keeps its own place
 * and the player's changes are held until it is on again
 * (MIDI-CHORD-REGIONS.md).
 *
 * Bare rings in the chart's header, a div, since App.css styles every button
 * as a raised pill; in the player, among its buttons, a button of their
 * style (`buttonStyle`), the rings black off and white glowing on.
 */
type FollowToggleProps = {
  on: boolean;
  onToggle: () => void;
  // The rings' size; the toggle is a little narrower than tall
  size?: string;
  buttonStyle?: React.CSSProperties;
};

const FollowToggle: React.FC<FollowToggleProps> = ({ on, onToggle, size = '0.9dvw', buttonStyle }) => {
  const title = on ? 'Follow: on. The chart and the MIDI file are linked' : 'Follow: off';
  const rings = (
    <>
      <span className="follow-ring follow-ring-top">{'O'}</span>
      <span className="follow-ring follow-ring-bottom">{'O'}</span>
    </>
  );
  if (buttonStyle) {
    return (
      <button
        className={`follow-toggle follow-toggle-button${on ? ' linked' : ''}`}
        aria-pressed={on}
        title={title}
        onClick={(e) => { e.stopPropagation(); onToggle(); }}
        style={{ ...buttonStyle, fontSize: size, flexDirection: 'column' }}
      >
        {rings}
      </button>
    );
  }
  return (
    <div
      role="switch"
      aria-checked={on}
      data-actionable="true"
      className={`follow-toggle${on ? ' linked' : ''}`}
      title={title}
      onClick={(e) => { e.stopPropagation(); onToggle(); }}
      style={{ fontSize: size }}
    >
      {rings}
    </div>
  );
};

export default FollowToggle;
